import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Trash2, Plus, LogOut, Upload, Save, Loader2, ArrowLeft, Check, Users, Clock, Eye } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { API, resolveUrl } from "../context/SiteDataContext";
import { BRAND } from "../data/content";
import { Stars } from "../components/Stars";

const inputCls =
  "w-full rounded-md border border-[#2E2418] bg-[#15100B] px-3 py-2 text-sm text-[#F5ECDA] outline-none focus:border-[#D4AF37] transition-colors";
const btnGold =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-black hover:bg-[#F3E5AB] transition-colors disabled:opacity-50";
const btnGhost =
  "inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-[#F5ECDA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors";

function LoginView() {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenue, Essi 👑");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0C0806] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-xl border border-[#2E2418] bg-[#15100B] p-8" data-testid="admin-login-form">
        <img src={BRAND.logo} alt="Slaly" className="mx-auto mb-6 h-16 w-auto rounded-md bg-[#F5ECDA] p-1.5 object-contain" />
        <h1 className="mb-1 text-center font-serif text-2xl text-[#F5ECDA]">Espace Admin</h1>
        <p className="mb-6 text-center text-xs uppercase tracking-[0.2em] text-[#B7A78C]">Slaly Hair & Beauty</p>
        <label className="mb-1 block text-xs uppercase tracking-wide text-[#B7A78C]">Email</label>
        <input className={inputCls + " mb-4"} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" />
        <label className="mb-1 block text-xs uppercase tracking-wide text-[#B7A78C]">Mot de passe</label>
        <input className={inputCls + " mb-6"} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" />
        <button className={btnGold + " w-full"} disabled={loading} data-testid="admin-login-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
        </button>
        <a href="/" className="mt-4 block text-center text-xs text-[#B7A78C] hover:text-[#D4AF37]">← Retour au site</a>
      </form>
    </div>
  );
}

function Section({ title, children, onSave, saving }) {
  return (
    <div className="rounded-xl border border-[#2E2418] bg-[#15100B] p-6">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#F5ECDA]">{title}</h2>
        {onSave && (
          <button className={btnGold} onClick={onSave} disabled={saving} data-testid={`save-${title}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Enregistrer</>}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function PhotosEditor({ authAxios, gallery, reload }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const addUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await authAxios().post("/admin/gallery", { url: url.trim() });
      setUrl("");
      toast.success("Photo ajoutée");
      reload();
    } catch (e) { toast.error("Échec de l'ajout"); } finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await authAxios().post("/admin/gallery/upload", fd);
      toast.success("Photo téléversée");
      reload();
    } catch (err) { toast.error("Échec du téléversement"); } finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    try {
      await authAxios().delete(`/admin/gallery/${id}`);
      toast.success("Photo supprimée");
      reload();
    } catch (e) { toast.error("Échec de la suppression"); }
  };

  return (
    <Section title="Photos">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <label className={btnGhost + " cursor-pointer"} data-testid="upload-photo-label">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser une photo
          <input type="file" accept="image/*" className="hidden" onChange={uploadFile} data-testid="upload-photo-input" />
        </label>
        <div className="flex flex-1 gap-2">
          <input className={inputCls} placeholder="… ou coller un lien d'image (https://)" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gallery-url-input" />
          <button className={btnGold} onClick={addUrl} disabled={busy} data-testid="add-url-btn"><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {gallery.map((g) => (
          <div key={g.id} className="group relative overflow-hidden rounded-md border border-[#2E2418]" data-testid={`admin-photo-${g.id}`}>
            <img src={resolveUrl(g.url)} alt="" className="aspect-square w-full object-cover" />
            <button onClick={() => remove(g.id)} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-red-400 opacity-0 transition-opacity group-hover:opacity-100" data-testid={`delete-photo-${g.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ServicesEditor({ authAxios, initial, reload }) {
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);
  const cats = [["african", "Tresses & Naturel"], ["european", "Styles Européens"], ["treatments", "Soins"]];

  const setItem = (cat, i, field, val) => {
    setData((d) => {
      const copy = { ...d, [cat]: d[cat].map((it, idx) => (idx === i ? { ...it, [field]: field === "price" ? Number(val) : val } : it)) };
      return copy;
    });
  };
  const addRow = (cat) => setData((d) => ({ ...d, [cat]: [...d[cat], { en: "", fr: "", price: 0 }] }));
  const removeRow = (cat, i) => setData((d) => ({ ...d, [cat]: d[cat].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await authAxios().put("/admin/services", data);
      toast.success("Services enregistrés");
      reload();
    } catch (e) { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); }
  };

  return (
    <Section title="Services & Prix" onSave={save} saving={saving}>
      <div className="space-y-6">
        {cats.map(([key, label]) => (
          <div key={key}>
            <h3 className="mb-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37]">{label}</h3>
            <div className="space-y-2">
              {data[key].map((it, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <input className={inputCls + " flex-1 min-w-[120px]"} placeholder="Nom (EN)" value={it.en} onChange={(e) => setItem(key, i, "en", e.target.value)} />
                  <input className={inputCls + " flex-1 min-w-[120px]"} placeholder="Nom (FR)" value={it.fr} onChange={(e) => setItem(key, i, "fr", e.target.value)} />
                  <div className="flex items-center gap-1">
                    <span className="text-[#B7A78C]">£</span>
                    <input className={inputCls + " w-20"} type="number" value={it.price} onChange={(e) => setItem(key, i, "price", e.target.value)} />
                  </div>
                  <button onClick={() => removeRow(key, i)} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={() => addRow(key)} className="mt-2 inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline"><Plus className="h-3.5 w-3.5" /> Ajouter</button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function HoursEditor({ authAxios, initial, reload }) {
  const [hours, setHours] = useState(initial);
  const [saving, setSaving] = useState(false);
  const setField = (i, path, val) => {
    setHours((h) => h.map((row, idx) => {
      if (idx !== i) return row;
      if (path === "appt") return { ...row, appt: val };
      return { ...row, time: { ...row.time, [path]: val } };
    }));
  };
  const save = async () => {
    setSaving(true);
    try {
      await authAxios().put("/admin/hours", { hours });
      toast.success("Horaires enregistrés");
      reload();
    } catch (e) { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); }
  };
  return (
    <Section title="Horaires" onSave={save} saving={saving}>
      <div className="space-y-2">
        {hours.map((row, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <span className="w-24 text-sm text-[#F5ECDA]">{row.day.fr}</span>
            <input className={inputCls + " flex-1 min-w-[110px]"} placeholder="Horaire (EN)" value={row.time.en} onChange={(e) => setField(i, "en", e.target.value)} />
            <input className={inputCls + " flex-1 min-w-[110px]"} placeholder="Horaire (FR)" value={row.time.fr} onChange={(e) => setField(i, "fr", e.target.value)} />
            <label className="flex items-center gap-1 text-xs text-[#B7A78C]">
              <input type="checkbox" checked={!!row.appt} onChange={(e) => setField(i, "appt", e.target.checked)} /> RDV
            </label>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ContactEditor({ authAxios, initial, reload }) {
  const [s, setS] = useState(initial);
  const [saving, setSaving] = useState(false);
  const fields = [
    ["instagram", "Instagram (lien)"], ["facebook", "Facebook (lien)"], ["tiktok", "TikTok (lien)"],
    ["email", "Email"], ["phoneDisplay", "Téléphone (affiché)"], ["tel", "Téléphone (lien tel:)"],
    ["whatsapp", "WhatsApp (numéro, ex: 447467591554)"], ["address", "Adresse"],
  ];
  const save = async () => {
    setSaving(true);
    try {
      await authAxios().put("/admin/social", {
        instagram: s.instagram || "", facebook: s.facebook || "", tiktok: s.tiktok || "",
        email: s.email || "", phoneDisplay: s.phoneDisplay || "", tel: s.tel || "",
        whatsapp: s.whatsapp || "", address: s.address || "",
      });
      toast.success("Coordonnées enregistrées");
      reload();
    } catch (e) { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); }
  };
  return (
    <Section title="Réseaux & Coordonnées" onSave={save} saving={saving}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map(([key, label]) => (
          <div key={key}>
            <label className="mb-1 block text-xs uppercase tracking-wide text-[#B7A78C]">{label}</label>
            <input className={inputCls} value={s[key] || ""} onChange={(e) => setS({ ...s, [key]: e.target.value })} data-testid={`contact-${key}`} />
          </div>
        ))}
      </div>
    </Section>
  );
}

function ProductRow({ authAxios, product, reload }) {
  const [p, setP] = useState(product);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await authAxios().post("/admin/upload", fd);
      setP((v) => ({ ...v, image: data.url }));
      toast.success("Image téléversée");
    } catch (err) { toast.error("Échec du téléversement"); } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await authAxios().put(`/admin/products/${p.id}`, { name: p.name, price: Number(p.price), description: p.description || "", image: p.image || "" });
      toast.success("Produit enregistré");
      reload();
    } catch (e) { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); }
  };
  const remove = async () => {
    try { await authAxios().delete(`/admin/products/${p.id}`); toast.success("Produit supprimé"); reload(); }
    catch (e) { toast.error("Échec de la suppression"); }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[#2E2418] p-4 sm:flex-row" data-testid={`admin-product-${p.id}`}>
      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md bg-[#1C1610]">
        {p.image ? <img src={resolveUrl(p.image)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-[#B7A78C]">—</div>}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <input className={inputCls + " flex-1 min-w-[140px]"} placeholder="Nom du produit" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
          <div className="flex items-center gap-1"><span className="text-[#B7A78C]">£</span>
            <input className={inputCls + " w-24"} type="number" value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} />
          </div>
        </div>
        <input className={inputCls} placeholder="Description" value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} />
        <div className="flex flex-wrap items-center gap-2">
          <input className={inputCls + " flex-1 min-w-[160px]"} placeholder="Lien image (https://) ou téléverser →" value={p.image || ""} onChange={(e) => setP({ ...p, image: e.target.value })} />
          <label className={btnGhost + " cursor-pointer"}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <input type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
          <button className={btnGold} onClick={save} disabled={saving} data-testid={`save-product-${p.id}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </button>
          <button onClick={remove} className="text-red-400 hover:text-red-300" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

function ProductsEditor({ authAxios, products, reload }) {
  const [adding, setAdding] = useState(false);
  const addNew = async () => {
    setAdding(true);
    try {
      await authAxios().post("/admin/products", { name: "Nouveau produit", price: 0, description: "", image: "" });
      toast.success("Produit ajouté — modifiez-le ci-dessous");
      reload();
    } catch (e) { toast.error("Échec de l'ajout"); } finally { setAdding(false); }
  };
  return (
    <Section title="Produits">
      <button className={btnGold + " mb-5"} onClick={addNew} disabled={adding} data-testid="add-product-btn">
        {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Ajouter un produit</>}
      </button>
      <div className="space-y-3">
        {products.map((p) => <ProductRow key={p.id} authAxios={authAxios} product={p} reload={reload} />)}
      </div>
    </Section>
  );
}

function ReviewAdminRow({ authAxios, r, load }) {
  const [reply, setReply] = useState(r.reply || "");
  const [saving, setSaving] = useState(false);
  const approve = async () => {
    try { await authAxios().put(`/admin/reviews/${r.id}/approve`); toast.success("Avis publié"); load(); }
    catch (e) { toast.error("Échec"); }
  };
  const remove = async () => {
    try { await authAxios().delete(`/admin/reviews/${r.id}`); toast.success("Avis supprimé"); load(); }
    catch (e) { toast.error("Échec"); }
  };
  const saveReply = async () => {
    setSaving(true);
    try { await authAxios().put(`/admin/reviews/${r.id}/reply`, { reply }); toast.success("Réponse enregistrée"); load(); }
    catch (e) { toast.error("Échec"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-[#2E2418] p-4" data-testid={`admin-review-${r.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-3">
            <span className="font-serif text-lg text-[#F5ECDA]">{r.name}</span>
            <Stars value={r.rating} size={14} />
            <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${r.status === "approved" ? "bg-green-900/40 text-green-300" : "bg-amber-900/40 text-amber-300"}`}>
              {r.status === "approved" ? "Publié" : "En attente"}
            </span>
          </div>
          <p className="text-sm text-[#D9CFB9]">"{r.message}"</p>
          {r.image && <img src={resolveUrl(r.image)} alt="" className="mt-2 h-20 w-20 rounded-md object-cover" />}
        </div>
        <div className="flex items-center gap-2">
          {r.status !== "approved" && (
            <button onClick={approve} className={btnGold} data-testid={`approve-review-${r.id}`}>
              <Check className="h-4 w-4" /> Publier
            </button>
          )}
          <button onClick={remove} className="text-red-400 hover:text-red-300" data-testid={`delete-review-${r.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
        <input className={inputCls + " flex-1 min-w-[200px]"} placeholder="Répondre publiquement à cet avis…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`reply-input-${r.id}`} />
        <button className={btnGhost} onClick={saveReply} disabled={saving} data-testid={`reply-save-${r.id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Répondre"}
        </button>
      </div>
    </div>
  );
}

function ReviewsEditor({ authAxios }) {
  const [reviews, setReviews] = useState(null);
  const load = async () => {
    try { const { data } = await authAxios().get("/admin/reviews"); setReviews(data); }
    catch (e) { toast.error("Échec du chargement des avis"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <Section title="Avis">
      {!reviews ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" /></div>
      ) : reviews.length === 0 ? (
        <p className="py-8 text-[#B7A78C]">Aucun avis pour l'instant.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => <ReviewAdminRow key={r.id} authAxios={authAxios} r={r} load={load} />)}
        </div>
      )}
    </Section>
  );
}

function Dashboard() {
  const { logout, authAxios } = useAdminAuth();
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("photos");

  const reload = async () => {
    const { data } = await axios.get(`${API}/content`);
    setContent(data);
    try { const { data: s } = await authAxios().get("/admin/stats"); setStats(s); } catch (e) {}
  };
  useEffect(() => { reload(); }, []);

  const tabs = [["photos", "Photos"], ["products", "Produits"], ["reviews", "Avis"], ["services", "Services"], ["hours", "Horaires"], ["contact", "Coordonnées"]];

  return (
    <div className="min-h-screen bg-[#0C0806] pb-20">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-[#2E2418] bg-[#0C0806]/90 px-6 py-4 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <img src={BRAND.logo} alt="Slaly" className="h-9 w-auto rounded bg-[#F5ECDA] p-0.5 object-contain" />
          <span className="font-serif text-lg text-[#F5ECDA]">Admin · Essi</span>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" className={btnGhost} data-testid="admin-view-site"><ArrowLeft className="h-4 w-4" /> Voir le site</a>
          <button onClick={logout} className={btnGhost} data-testid="admin-logout"><LogOut className="h-4 w-4" /> Déconnexion</button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 pt-8">
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-[#2E2418] bg-[#15100B] p-4" data-testid="stat-visits">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#B7A78C]"><Users className="h-3.5 w-3.5 text-[#D4AF37]" /> Visites</div>
            <div className="mt-1 font-serif text-3xl text-[#F5ECDA]">{stats ? stats.visits : "—"}</div>
          </div>
          <div className="rounded-lg border border-[#2E2418] bg-[#15100B] p-4" data-testid="stat-pending">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#B7A78C]"><Clock className="h-3.5 w-3.5 text-[#D4AF37]" /> Avis en attente</div>
            <div className="mt-1 font-serif text-3xl text-[#F5ECDA]">{stats ? stats.reviews_pending : "—"}</div>
          </div>
          <div className="rounded-lg border border-[#2E2418] bg-[#15100B] p-4" data-testid="stat-products">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#B7A78C]"><Eye className="h-3.5 w-3.5 text-[#D4AF37]" /> Produits</div>
            <div className="mt-1 font-serif text-3xl text-[#F5ECDA]">{content ? (content.products || []).length : "—"}</div>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors ${tab === key ? "bg-[#D4AF37] text-black" : "border border-white/15 text-[#B7A78C] hover:text-[#F5ECDA]"}`}
              data-testid={`tab-${key}`}>
              {label}
            </button>
          ))}
        </div>

        {!content ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
        ) : (
          <>
            {tab === "photos" && <PhotosEditor authAxios={authAxios} gallery={content.gallery} reload={reload} />}
            {tab === "products" && <ProductsEditor authAxios={authAxios} products={content.products || []} reload={reload} />}
            {tab === "reviews" && <ReviewsEditor authAxios={authAxios} />}
            {tab === "services" && <ServicesEditor authAxios={authAxios} initial={content.services} reload={reload} />}
            {tab === "hours" && <HoursEditor authAxios={authAxios} initial={content.hours} reload={reload} />}
            {tab === "contact" && <ContactEditor authAxios={authAxios} initial={content.social} reload={reload} />}
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { isAuthed } = useAdminAuth();
  return (
    <>
      <Toaster position="top-center" theme="dark" richColors />
      {isAuthed ? <Dashboard /> : <LoginView />}
    </>
  );
}
